import {
  Filter,
  type SelectItem,
  useSelectQueryGate,
} from '@vrplatform/components';
import { AutomationIcon } from '@vrplatform/components-legacy';
import {
  useAccountingConnectionQuery,
  useAutomationsQuery,
} from '@vrplatform/data';
import { hasValue, type Maybe } from '@vrplatform/utils';
import { useMemo } from 'react';
import type { BaseCustomFilterProps } from './_types';

interface Props extends BaseCustomFilterProps {
  automationIds: Maybe<Maybe<string>[]>;
  setAutomationIds: (ids: string[]) => void;
}

export const AutomationFilter = ({
  automationIds,
  setAutomationIds,
  label = 'Automation',
}: Props) => {
  const normalizedAutomationIds = (automationIds || []).filter(hasValue);
  const gate = useSelectQueryGate({ value: normalizedAutomationIds });
  const {
    data: automations = [],
    isLoading: loading,
    error,
  } = useAutomationsQuery(undefined, { skip: !gate.shouldFetchAny });
  const { accounting } = useAccountingConnectionQuery();

  const options = useMemo(() => {
    const filtered = automations.filter((automation) => {
      const isAccountingAutomation = automation.template.uniqueRef
        ?.toLowerCase()
        ?.includes('accounting');

      if (!isAccountingAutomation) return true;

      return [
        automation.leftConnectionId,
        automation.rightConnectionId,
      ].includes(accounting?.id || '');
    });

    return filtered.map<SelectItem>((automation) => ({
      label: automation.title || '',
      value: automation.automationId,
      icon: (
        <AutomationIcon templateType={automation.template.type} size={16} />
      ),
    }));
  }, [automations, accounting]);

  const value = useMemo(
    () =>
      gate.hasValue
        ? options.filter((option) =>
            normalizedAutomationIds.includes(option.value)
          )
        : [],
    [gate.hasValue, normalizedAutomationIds, options]
  );

  return (
    <Filter.Select
      value={value}
      setValue={(next) => setAutomationIds(next.map((item) => item.value))}
      label={label}
      type="multiple"
      withinPortal
      onOpenChange={gate.onOpenChange}
      data={{
        options,
        loading,
        error,
      }}
    />
  );
};
